/**
 * lib/auditPack.ts
 * ------------------------------------------------------------------
 * THE AUDITOR'S PACK — a period, one action, everything they will ask for.
 *
 * An audit is a list of questions the school already has the answers to,
 * asked one at a time over three weeks. The trial balance; then the
 * ledger behind a figure on it; then the reconciliation proving that
 * ledger agrees with the detail; then the paper behind one transaction in
 * that detail. Every request is somebody finding a report, exporting it
 * and emailing it, and the three weeks are mostly waiting.
 *
 * IT COMPUTES NOTHING OF ITS OWN. Every figure comes from the shipped
 * module the corresponding screen already uses — glReport,
 * financialStatements, controlAccounts, debtors, vouchers, pettyCash,
 * vat. A pack that recomputed its own totals would be a second opinion,
 * and the moment it disagreed with a screen nobody could say which was
 * the school's actual position. That constraint is the whole design:
 * adding a section here is a table definition, never another calculation.
 *
 * ONE DEFINITION PER SECTION, in the `ExportColumn` shape lib/tableExport
 * already renders as both a PDF and a CSV — so the auditor's spreadsheet
 * cannot disagree with the auditor's PDF.
 *
 * THE MANIFEST IS PART OF THE PACK, NOT A COURTESY. It records the period,
 * when it was produced, which sections are present, and — the part that
 * matters — what was deliberately LEFT OUT and why. A pack that quietly
 * omits a section looks complete and is not; an auditor who finds the
 * omission themselves stops trusting the rest of it.
 *
 * PURE. Data in, section definitions out. No Firestore, no React, no zip.
 * ------------------------------------------------------------------ */
import type { ExportColumn } from '@/lib/tableExport';
import type { TrialBalance, TrialBalanceRow } from '@/lib/glReport';
import type { IncomeStatement, BalanceSheet, StatementLine } from '@/lib/financialStatements';
import type { ControlReport, ControlCheck } from '@/lib/controlAccounts';
import type { DebtorAccount } from '@/lib/debtors';
import type { CreditorSummary } from '@/lib/vouchers';
import type { VatReturn } from '@/lib/vat';
import type { FloatState, PettyCount } from '@/lib/pettyCash';
import type { Finding } from '@/lib/anomalies';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n: number) =>
  `${n < 0 ? '-' : ''}P${Math.abs(round2(n)).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A section of the pack: a title, a table, and why it is here. */
export interface PackSection<T = unknown> {
  key: string;
  title: string;
  /** What question this section answers, printed under the title. */
  purpose: string;
  columns: ExportColumn<T>[];
  rows: T[];
  totals?: (string | number)[];
  /** A caveat the auditor should read before the figures. */
  note?: string;
}

export interface PackPeriod {
  from: string;
  to: string;
  label: string;
}

export interface PackSources {
  period: PackPeriod;
  trialBalance: TrialBalance;
  income?: IncomeStatement;
  balanceSheet?: BalanceSheet;
  control?: ControlReport;
  debtors?: DebtorAccount[];
  creditors?: CreditorSummary[];
  vat?: VatReturn | null;
  pettyFloats?: { name: string; custodianName: string; state: FloatState; counts: PettyCount[] }[];
  findings?: Finding[];
}

/* =============================== sections =============================== */

function trialBalanceSection(tb: TrialBalance): PackSection<TrialBalanceRow> {
  return {
    key: 'trial-balance',
    title: 'Trial balance',
    purpose: 'Every account with a movement in the period, and proof the ledger balances.',
    columns: [
      { header: 'Code', text: (r) => r.code },
      { header: 'Account', text: (r) => r.name },
      { header: 'Type', text: (r) => r.type },
      { header: 'Entries', text: (r) => String(r.entries), align: 'right' },
      { header: 'Debit', text: (r) => (r.dr ? money(r.dr) : ''), raw: (r) => r.dr || null, align: 'right' },
      { header: 'Credit', text: (r) => (r.cr ? money(r.cr) : ''), raw: (r) => r.cr || null, align: 'right' },
    ],
    rows: tb.rows,
    totals: ['', '', '', '', money(tb.totalDr), money(tb.totalCr)],
    /* Stated on the page rather than left to be noticed. Every journal is
       asserted balanced before it is written, so an out-of-balance total
       is not a rounding curiosity — it means a journal was written around
       that check, or lines have been lost. */
    note: tb.balanced
      ? 'The ledger balances.'
      : `THE LEDGER DOES NOT BALANCE — debits exceed credits by ${money(tb.difference)}. `
        + 'Every journal is checked before it is written, so this means a journal bypassed '
        + 'that check or lines are missing. The rest of this pack rests on figures that '
        + 'do not add up.',
  };
}

function statementSection(
  key: string, title: string, purpose: string, lines: StatementLine[], total: number,
): PackSection<StatementLine> {
  return {
    key,
    title,
    purpose,
    columns: [
      { header: 'Code', text: (l) => l.code },
      { header: 'Line', text: (l) => l.name },
      { header: 'Entries', text: (l) => String(l.entries), align: 'right' },
      { header: 'Amount', text: (l) => money(l.amount), raw: (l) => l.amount, align: 'right' },
    ],
    rows: lines,
    totals: ['', 'Total', '', money(total)],
  };
}

function controlSection(report: ControlReport): PackSection<ControlCheck> {
  return {
    key: 'control-accounts',
    title: 'Control account reconciliations',
    purpose: 'Does each ledger control agree with the sub-ledger behind it.',
    columns: [
      { header: 'Control', text: (c) => c.label },
      { header: 'State', text: (c) => c.state },
      { header: 'Ledger', text: (c) => money(c.glBalance), raw: (c) => c.glBalance, align: 'right' },
      { header: 'Sub-ledger', text: (c) => money(c.subLedger), raw: (c) => c.subLedger, align: 'right' },
      { header: 'Difference', text: (c) => money(c.difference), raw: (c) => c.difference, align: 'right' },
      { header: 'What the sub-ledger is', text: (c) => c.detailIs },
    ],
    rows: report.checks,
    /* Named, not hidden. A pack that showed only the controls this system
       can answer would imply the rest reconcile. */
    note: report.checks.some((c) => c.state === 'awaiting-module')
      ? 'Controls marked "awaiting-module" are not yet answerable by this system and have '
        + 'NOT been reconciled. They are listed so their absence is visible.'
      : undefined,
  };
}

function debtorsSection(rows: DebtorAccount[]): PackSection<DebtorAccount> {
  return {
    key: 'debtors',
    title: 'Debtors',
    purpose: 'What families owed at the end of the period, and how long it has been owed.',
    columns: [
      { header: 'Family', text: (d) => d.name },
      { header: 'Pupils', text: (d) => String(d.pupils?.length ?? 0), align: 'right' },
      { header: 'Status', text: (d) => d.status },
      { header: 'Billed', text: (d) => money(d.billed), raw: (d) => d.billed, align: 'right' },
      { header: 'Paid', text: (d) => money(d.paid), raw: (d) => d.paid, align: 'right' },
      { header: 'Oldest (days)', text: (d) => String(d.oldestDays), align: 'right' },
      { header: 'Balance', text: (d) => money(d.balance), raw: (d) => d.balance, align: 'right' },
    ],
    rows,
    totals: ['Total', '', '', '', '', '',
      money(rows.reduce((n, d) => n + d.balance, 0))],
  };
}

function creditorsSection(rows: CreditorSummary[]): PackSection<CreditorSummary> {
  return {
    key: 'creditors',
    title: 'Creditors',
    purpose: 'What the school owed suppliers at the end of the period.',
    columns: [
      { header: 'Creditor', text: (c) => c.name },
      { header: 'Registered', text: (c) => (c.supplierId ? 'yes' : 'no') },
      { header: 'Bills', text: (c) => String(c.vouchers.length), align: 'right' },
      { header: 'Oldest (days)', text: (c) => String(c.oldestDays), align: 'right' },
      { header: 'Owed', text: (c) => money(c.total), raw: (c) => c.total, align: 'right' },
    ],
    rows,
    totals: ['Total', '', '', '', money(rows.reduce((n, c) => n + c.total, 0))],
  };
}

function vatSection(ret: VatReturn): PackSection<{ label: string; amount: number }> {
  const rows = [
    { label: 'Output tax charged', amount: ret.outputTax },
    { label: 'Input tax paid', amount: ret.inputTaxPaid },
    { label: 'Input tax not reclaimable (exempt supplies)', amount: ret.inputTaxIrrecoverable },
    { label: 'Input tax reclaimed', amount: ret.inputTaxRecoverable },
  ];
  return {
    key: 'vat',
    title: 'VAT return',
    purpose: 'Tax charged and reclaimed in the period, read off the VAT account balances.',
    columns: [
      { header: 'Line', text: (r) => r.label },
      { header: 'Amount', text: (r) => money(r.amount), raw: (r) => r.amount, align: 'right' },
    ],
    rows,
    totals: [ret.net >= 0 ? 'Payable to BURS' : 'Refund due', money(Math.abs(ret.net))],
    note: ret.hasPreRegistration
      ? 'Some entries in this period predate the registration date.'
      : undefined,
  };
}

interface PettyRow {
  name: string; custodian: string; float: number; cash: number;
  unclaimed: number; variance: number; counts: number;
}

function pettySection(
  floats: NonNullable<PackSources['pettyFloats']>,
): PackSection<PettyRow> {
  const rows: PettyRow[] = floats.map((f) => ({
    name: f.name,
    custodian: f.custodianName,
    float: f.state.floatAmount,
    cash: f.state.cashOnHand,
    unclaimed: f.state.unreimbursedTotal,
    variance: f.state.countVariance,
    counts: f.counts.length,
  }));
  return {
    key: 'petty-cash',
    title: 'Petty cash',
    purpose: 'Each float, what should be in it, and whether it has been counted.',
    columns: [
      { header: 'Float', text: (r) => r.name },
      { header: 'Custodian', text: (r) => r.custodian },
      { header: 'Float', text: (r) => money(r.float), raw: (r) => r.float, align: 'right' },
      { header: 'Cash on hand', text: (r) => money(r.cash), raw: (r) => r.cash, align: 'right' },
      { header: 'Unclaimed slips', text: (r) => money(r.unclaimed), raw: (r) => r.unclaimed, align: 'right' },
      { header: 'Count variance', text: (r) => money(r.variance), raw: (r) => r.variance, align: 'right' },
      { header: 'Counts', text: (r) => String(r.counts), align: 'right' },
    ],
    rows,
    /* An uncounted float is the finding, and an auditor will look for it. */
    note: rows.some((r) => r.counts === 0)
      ? 'A float showing no counts has never been physically verified.'
      : undefined,
  };
}

function findingsSection(findings: Finding[]): PackSection<Finding> {
  return {
    key: 'anomalies',
    title: 'Anomaly review',
    purpose: 'Questions the system raised about its own data, and their state.',
    columns: [
      { header: 'Check', text: (f) => f.title },
      { header: 'Severity', text: (f) => f.severity },
      { header: 'What was found', text: (f) => f.detail ?? '' },
    ],
    rows: findings,
    note: 'These are questions, not errors. An empty list means nothing was flagged, '
      + 'not that nothing is wrong.',
  };
}

/* =============================== the pack =============================== */

export function buildAuditPack(src: PackSources): PackSection<never>[] {
  const out: PackSection<never>[] = [];
  const add = <T>(s: PackSection<T>) => out.push(s as unknown as PackSection<never>);

  add(trialBalanceSection(src.trialBalance));

  if (src.income) {
    add(statementSection('income-statement', 'Income and expenditure',
      'What the school earned and spent in the period.',
      [...src.income.income.lines, ...src.income.expenditure.lines], src.income.surplus));
  }
  if (src.balanceSheet) {
    add(statementSection('balance-sheet', 'Balance sheet',
      'What the school owns and owes at the end of the period.',
      [...src.balanceSheet.assets.lines, ...src.balanceSheet.liabilities.lines,
        ...src.balanceSheet.equity.lines], src.balanceSheet.netAssets));
  }
  if (src.control) add(controlSection(src.control));
  if (src.debtors?.length) add(debtorsSection(src.debtors));
  if (src.creditors?.length) add(creditorsSection(src.creditors));
  if (src.vat) add(vatSection(src.vat));
  if (src.pettyFloats?.length) add(pettySection(src.pettyFloats));
  if (src.findings) add(findingsSection(src.findings));

  return out;
}

/**
 * What is in the pack, and what is not.
 *
 * The omissions are the point. An auditor who discovers a missing section
 * themselves stops trusting the sections that are there.
 */
export function packManifest(
  src: PackSources,
  sections: PackSection<never>[],
  schoolName: string,
  producedBy?: string,
): string {
  const lines: string[] = [
    `${schoolName} — AUDIT PACK`,
    `Period: ${src.period.label} (${src.period.from} to ${src.period.to})`,
    `Produced: ${new Date().toISOString().slice(0, 19).replace('T', ' ')} UTC`,
    producedBy ? `Produced by: ${producedBy}` : '',
    '',
    'HOW THESE FIGURES WERE DERIVED',
    'Every figure is produced by the same module the corresponding screen in the',
    'portal uses. Nothing in this pack is recomputed for it, so a figure here and',
    'the same figure on screen cannot differ.',
    '',
    'SECTIONS INCLUDED',
    ...sections.map((s) => `  - ${s.title} (${s.rows.length} rows) — ${s.purpose}`),
    '',
  ];

  const missing: string[] = [];
  if (!src.income) missing.push('Income and expenditure — no income statement was supplied.');
  if (!src.balanceSheet) missing.push('Balance sheet — none was supplied.');
  if (!src.control) missing.push('Control account reconciliations — not run for this period.');
  if (!src.debtors?.length) missing.push('Debtors — nothing was outstanding, or the list was not supplied.');
  if (!src.creditors?.length) missing.push('Creditors — nothing was outstanding, or the list was not supplied.');
  if (!src.vat) missing.push('VAT — the school is not registered, so there is no return.');
  if (!src.pettyFloats?.length) missing.push('Petty cash — no float has been opened.');

  if (missing.length) {
    lines.push('DELIBERATELY NOT INCLUDED', ...missing.map((m) => `  - ${m}`), '');
  }

  if (!src.trialBalance.balanced) {
    lines.push(
      'WARNING',
      `  The trial balance does not balance. Debits exceed credits by`,
      `  ${money(src.trialBalance.difference)}. Everything in this pack rests on it.`,
      '',
    );
  }

  return lines.filter((l) => l !== '' || true).join('\n');
}
